import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { classNames } from '../utils/tailwind/classNames';

interface RecaptchaV2WidgetProps {
    siteKey: string;
    onToken: (token: string) => void;
    onExpired?: () => void;
    onError?: () => void;
    theme?: 'light' | 'dark';
    size?: 'compact' | 'normal';
    tabindex?: number;
    className?: string;
}

export interface RecaptchaV2WidgetRef {
    reset: () => void;
}

export const RecaptchaV2Widget = forwardRef<RecaptchaV2WidgetRef, RecaptchaV2WidgetProps>(
    (
        {
            siteKey,
            onToken,
            onExpired,
            onError,
            theme = 'light',
            size = 'normal',
            tabindex = 0,
            className,
        },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const [widgetId, setWidgetId] = useState<number | null>(null);

        useImperativeHandle(
            ref,
            () => ({
                reset: (): void => {
                    if (widgetId !== null && window.grecaptcha) {
                        try {
                            window.grecaptcha.enterprise.reset(widgetId);
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error('Failed to reset reCAPTCHA v2 widget:', error);
                        }
                    }
                },
            }),
            [widgetId],
        );

        useEffect(() => {
            const loadRecaptcha = (): void => {
                if (
                    window.grecaptcha &&
                    containerRef.current &&
                    containerRef.current.childElementCount === 0
                ) {
                    try {
                        const id = window.grecaptcha.enterprise.render(containerRef.current, {
                            sitekey: siteKey,
                            callback: onToken,
                            'expired-callback': onExpired,
                            'error-callback': onError,
                            theme: theme || 'light',
                            size: size || 'normal',
                            tabindex: tabindex || 0,
                        });
                        setWidgetId(id);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error('Failed to render reCAPTCHA v2 widget:', error);
                        onError?.();
                    }
                }
            };

            if (window.grecaptcha) {
                loadRecaptcha();
            } else {
                // Wait for grecaptcha to load
                const checkInterval = setInterval(() => {
                    if (window.grecaptcha) {
                        clearInterval(checkInterval);
                        loadRecaptcha();
                    }
                }, 100);

                return (): void => {
                    clearInterval(checkInterval);
                };
            }

            return undefined;
        }, [siteKey, onToken, onExpired, onError, theme, size, tabindex, widgetId]);

        return (
            <div
                ref={containerRef}
                className={classNames(widgetId !== null ? 'block' : 'hidden', className)}
                data-testid="recaptcha-v2-widget"
            />
        );
    },
);

RecaptchaV2Widget.displayName = 'RecaptchaV2Widget';

RecaptchaV2Widget.defaultProps = {
    theme: 'light',
    size: 'normal',
    tabindex: 0,
    onExpired: undefined,
    onError: undefined,
    className: undefined,
};
